import React from 'react';
import { Box, Typography, Grid, Skeleton, Chip, Divider, TextField, MenuItem } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { youtubeAPI, dataAPI } from '../../services/api';
import { TrendingUp, ThumbUp, Comment, AccessTime, Public, Tag, Timer } from '@mui/icons-material';
import SectionTitle from '../crt/SectionTitle';
import Panel from '../crt/Panel';

const ContentStrategy: React.FC = () => {
  const [days, setDays] = React.useState(60);
  const [topicDays, setTopicDays] = React.useState(365);
  const [minSupport, setMinSupport] = React.useState(3);
  const [perfDays, setPerfDays] = React.useState(30);

  const { data: engagement, isLoading: loadingEng } = useQuery({
    queryKey: ['yt-engagement-summary', 60],
    queryFn: () => youtubeAPI.getEngagementSummary(60),
    staleTime: 60000,
  });
  const { data: cohorts, isLoading: loadingCohorts } = useQuery({
    queryKey: ['yt-cohorts-simple', 12],
    queryFn: () => youtubeAPI.getCohortsSimple(12),
    staleTime: 60000,
  });
  const { data: cadence, isLoading: loadingCadence } = useQuery({
    queryKey: ['yt-cadence-heatmap'],
    queryFn: () => youtubeAPI.getCadenceHeatmap(),
    staleTime: 60000,
  });
  const { data: sentiment, isLoading: loadingSent } = useQuery({
    queryKey: ['yt-sentiment', 30],
    queryFn: () => youtubeAPI.getSentiment(30),
    staleTime: 60000,
  });
  const { data: inventory, isLoading: loadingInv } = useQuery({
    queryKey: ['data-inventory', 'youtube-defaults'],
    queryFn: () => dataAPI.getInventory(undefined, true),
    staleTime: 60000,
  });

  const { data: earlyPerf, isLoading: loadingPerf } = useQuery({
    queryKey: ['yt-early-performance', perfDays],
    queryFn: () => youtubeAPI.getEarlyPerformance(perfDays, 50),
    staleTime: 60000,
  });
  const { data: topics, isLoading: loadingTopics } = useQuery({
    queryKey: ['yt-topics', topicDays, minSupport],
    queryFn: () => youtubeAPI.getTopics(topicDays, minSupport, 50),
    staleTime: 60000,
  });
  const { data: trafficSources } = useQuery({
    queryKey: ['yt-traffic-sources', 30],
    queryFn: () => youtubeAPI.getTrafficSources(30),
    staleTime: 60000,
  });
  const { data: playbackLocations } = useQuery({
    queryKey: ['yt-playback-locations', 30],
    queryFn: () => youtubeAPI.getPlaybackLocations(30),
    staleTime: 60000,
  });
  const { data: devicesOS } = useQuery({
    queryKey: ['yt-devices-os', 30],
    queryFn: () => youtubeAPI.getDevicesOS(30),
    staleTime: 60000,
  });
  const { data: geo } = useQuery({
    queryKey: ['yt-geography', 30],
    queryFn: () => youtubeAPI.getGeography(30),
    staleTime: 60000,
  });
  const { data: searchTerms } = useQuery({
    queryKey: ['yt-search-terms', 90],
    queryFn: () => youtubeAPI.getSearchTerms(90, 50),
    staleTime: 60000,
  });
  const { data: contentVelocity } = useQuery({
    queryKey: ['yt-content-velocity', 365],
    queryFn: () => youtubeAPI.getContentVelocity(365),
    staleTime: 60000,
  });
  const { data: avgVDur } = useQuery({
    queryKey: ['yt-avg-view-duration', 30],
    queryFn: () => youtubeAPI.getAvgViewDuration(30),
    staleTime: 60000,
  });
  const { data: shortsSplit } = useQuery({
    queryKey: ['yt-shorts-split', 365],
    queryFn: () => youtubeAPI.getShortsVsLongform(365),
    staleTime: 60000,
  });

  const loading = loadingEng || loadingCohorts || loadingCadence || loadingSent || loadingInv;

  return (
    <Box>
      <SectionTitle>GROWTH &amp; STRATEGY INSIGHTS</SectionTitle>

      <Grid container spacing={3}>
        {/* Filters Row */}
        <Grid size={{ xs: 12 }}>
          <Panel label="FILTERS" sx={{ p: 2, mb: 1 }}>
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <TextField size="small" select label="Perf Window" value={perfDays} onChange={e => setPerfDays(parseInt(e.target.value, 10))}>
                {[7, 14, 30, 60, 90].map(v => <MenuItem key={v} value={v}>{v} days</MenuItem>)}
              </TextField>
              <TextField size="small" select label="Topic Window" value={topicDays} onChange={e => setTopicDays(parseInt(e.target.value, 10))}>
                {[90, 180, 365].map(v => <MenuItem key={v} value={v}>{v} days</MenuItem>)}
              </TextField>
              <TextField size="small" select label="Min Topic Support" value={minSupport} onChange={e => setMinSupport(parseInt(e.target.value, 10))}>
                {[2,3,5,8].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Box>
          </Panel>
        </Grid>
        {/* Engagement Summary */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="ENGAGEMENT SUMMARY (60D)" sx={{ height: '100%' }}>
            {loadingEng ? (
              <Skeleton variant="rectangular" height={140} />
            ) : (
              <Box display="flex" gap={3} flexWrap="wrap">
                <Chip icon={<TrendingUp />} label={`Engagement: ${engagement?.overall?.engagement_rate_pct ?? 0}%`} color="primary" />
                <Chip icon={<ThumbUp />} label={`Likes / Views: ${engagement?.overall?.like_view_ratio_pct ?? 0}%`} />
                <Chip icon={<Comment />} label={`Comments / Views: ${engagement?.overall?.comment_view_ratio_pct ?? 0}%`} />
                <Chip icon={<AccessTime />} label={`Views: ${(engagement?.overall?.views ?? 0).toLocaleString()}`} />
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="textSecondary">
              Track engagement rate and quality ratios (likes/comments per view). Aim to improve baseline week over week.
            </Typography>
          </Panel>
        </Grid>

        {/* Sentiment */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="AUDIENCE SENTIMENT (30D)" sx={{ height: '100%' }}>
            {loadingSent ? (
              <Skeleton variant="rectangular" height={140} />
            ) : (
              <Box display="flex" gap={2}>
                <Chip label={`Positive ${sentiment?.summary?.positive ?? 0}%`} color="success" />
                <Chip label={`Neutral ${sentiment?.summary?.neutral ?? 0}%`} />
                <Chip label={`Negative ${sentiment?.summary?.negative ?? 0}%`} color="error" />
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="textSecondary">
              Surface common positive/negative themes from comments to guide content and CTAs.
            </Typography>
          </Panel>
        </Grid>

        {/* Cohorts */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="PUBLISH COHORTS (LAST 12 MONTHS)" sx={{ height: '100%' }}>
            {loadingCohorts ? (
              <Skeleton variant="rectangular" height={180} />
            ) : (
              <Box>
                {(cohorts?.cohorts || []).slice(0, 6).map((c: any) => (
                  <Box key={c.publish_month} display="flex" justifyContent="space-between" py={0.5}>
                    <Typography>{new Date(c.publish_month).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</Typography>
                    <Box>
                      <Chip size="small" label={`Videos ${c.videos}`} sx={{ mr: 1 }} />
                      <Chip size="small" label={`Median ${Number(c.median_views).toLocaleString()}`} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="textSecondary">
              Compare cohorts by month to evaluate content momentum and topic changes.
            </Typography>
          </Panel>
        </Grid>

        {/* Cadence */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="PUBLISHING CADENCE" sx={{ height: '100%' }}>
            {loadingCadence ? (
              <Skeleton variant="rectangular" height={180} />
            ) : (
              <Box>
                {(cadence?.heatmap || []).slice(0, 10).map((r: any, idx: number) => (
                  <Box key={idx} display="flex" justifyContent="space-between" py={0.5}>
                    <Typography>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][r.weekday]} @ {r.hour}:00</Typography>
                    <Box>
                      <Chip size="small" label={`Videos ${r.videos}`} sx={{ mr: 1 }} />
                      <Chip size="small" label={`Avg views ${Number(r.avg_views).toFixed(0)}`} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="textSecondary">
              Use day/hour slots with higher avg views as posting targets; A/B test timing.
            </Typography>
          </Panel>
        </Grid>

        {/* Early Performance Leaderboard */}
        <Grid size={{ xs: 12 }}>
          <Panel label="EARLY PERFORMANCE (7D/30D VIEWS, TIME TO 1K/5K/10K)">
            {loadingPerf ? (
              <Skeleton variant="rectangular" height={180} />
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {(earlyPerf?.videos || []).slice(0, 10).map((v: any) => (
                  <Chip key={v.video_id} icon={<Timer />} label={`${v.title.substring(0, 30)}… | 7d ${v.views_7d} | 30d ${v.views_30d} | 1k ${v.time_to_1k_days?.toFixed?.(1) ?? '—'}d`} />
                ))}
              </Box>
            )}
          </Panel>
        </Grid>

        {/* Topics vs Performance */}
        <Grid size={{ xs: 12 }}>
          <Panel label={`TOPICS BY VIEWS (LAST ${topicDays} DAYS)`}>
            {loadingTopics ? (
              <Skeleton variant="rectangular" height={180} />
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {(topics?.topics || []).slice(0, 30).map((t: any) => (
                  <Chip key={t.token} icon={<Tag />} label={`${t.token} • ${t.videos} vids • ${Number(t.engagement_rate*100).toFixed(1)}% ER`} />
                ))}
              </Box>
            )}
          </Panel>
        </Grid>

        {/* Traffic Sources */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="TRAFFIC SOURCES (30D)">
            <Box display="flex" flexWrap="wrap" gap={1}>
              {(trafficSources?.sources || []).slice(0, 12).map((s: any) => (
                <Chip key={(s.traffic_source || s.source) + String(s.views)} label={`${s.traffic_source || s.source}: ${Number(s.views).toLocaleString()} views`} />
              ))}
            </Box>
          </Panel>
        </Grid>

        {/* Playback Locations */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="PLAYBACK LOCATIONS (30D)">
            <Box display="flex" flexWrap="wrap" gap={1}>
              {(playbackLocations?.locations || []).slice(0, 12).map((s: any) => (
                <Chip key={s.location + String(s.views)} label={`${s.location}: ${Number(s.views).toLocaleString()} views`} />
              ))}
            </Box>
          </Panel>
        </Grid>

        {/* Devices & OS */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="DEVICES & OS (30D)">
            <Box display="flex" flexWrap="wrap" gap={1}>
              {(devicesOS?.devices || []).slice(0, 12).map((d: any) => (
                <Chip key={`${d.device_type}-${d.operating_system}-${d.views}`} label={`${d.device_type} ${d.operating_system}: ${Number(d.views).toLocaleString()}`} />
              ))}
            </Box>
          </Panel>
        </Grid>

        {/* Geography */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="TOP COUNTRIES (30D)">
            <Box display="flex" flexWrap="wrap" gap={1}>
              {(geo?.countries || []).slice(0, 12).map((g: any) => (
                <Chip key={g.country + String(g.views)} label={`${g.country}: ${Number(g.views).toLocaleString()} views`} />
              ))}
            </Box>
          </Panel>
        </Grid>

        {/* Search Terms */}
        <Grid size={{ xs: 12 }}>
          <Panel label="TOP SEARCH TERMS (90D)">
            <Box display="flex" flexWrap="wrap" gap={1}>
              {(searchTerms?.terms || []).slice(0, 24).map((t: any) => (
                <Chip key={t.term + String(t.views)} label={`${t.term}: ${Number(t.views).toLocaleString()} views`} />
              ))}
            </Box>
          </Panel>
        </Grid>

        {/* Content Velocity & Avg View Duration */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="CONTENT VELOCITY (WEEKLY)">
            <Box display="flex" flexDirection="column" gap={0.5}>
              {(contentVelocity?.weeks || []).slice(0, 8).map((w: any) => (
                <Typography key={w.week} variant="body2">{w.week}: {w.uploads} uploads • {Number(w.views).toLocaleString()} views</Typography>
              ))}
            </Box>
          </Panel>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="AVG VIEW DURATION (30D)">
            <Typography variant="h4" fontWeight={700}>{Math.round(avgVDur?.avg_seconds || 0)}s</Typography>
          </Panel>
        </Grid>

        {/* Shorts vs Longform */}
        <Grid size={{ xs: 12 }}>
          <Panel label="SHORTS VS LONGFORM (VIEWS)">
            <Box display="flex" gap={1}>
              {(shortsSplit?.split || []).map((s: any) => (
                <Chip key={s.type + String(s.views)} label={`${s.type}: ${Number(s.views).toLocaleString()} views (${s.videos} videos)`} />
              ))}
            </Box>
          </Panel>
        </Grid>

        {/* Data Inventory (non-empty tables) */}
        <Grid size={{ xs: 12 }}>
          <Panel label="DATA INVENTORY (YOUTUBE SCHEMAS)">
            {loadingInv ? (
              <Skeleton variant="rectangular" height={120} />
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {(inventory?.tables || []).filter((t: any) => t.has_data).map((t: any) => (
                  <Chip key={`${t.schema}.${t.table}`} icon={<Public />} label={`${t.schema}.${t.table} (${t.row_estimate})`} />
                ))}
              </Box>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContentStrategy;
